import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { apiClient } from '../shared/lib/apiClient';

const ThemeContext = createContext(null);

// Kept in sync with the inline blocking script in index.html.
export const THEME_STORAGE_KEY = 'tgmsaas.theme';
export const THEMES = ['light', 'dark', 'system'];

function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyThemeToDocument(theme) {
  const resolved = theme === 'system' ? getSystemTheme() : theme;
  document.documentElement.setAttribute('data-theme', resolved);
}

/**
 * Light/dark/system theme. Persisted to localStorage immediately (read by
 * the blocking script in index.html to avoid a flash of the wrong theme),
 * and synced to the backend once a user is authenticated via
 * PATCH /users/me. The API sync is best-effort: a failed sync never blocks
 * or reverts the locally-applied theme.
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return 'system';
    try {
      return window.localStorage.getItem(THEME_STORAGE_KEY) || 'system';
    } catch (e) {
      return 'system';
    }
  });
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    applyThemeToDocument(theme);
    if (theme !== 'system' || typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => applyThemeToDocument('system');
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Adopt the server-side preference once we know who the user is (e.g.
  // right after login, or after the silent refresh on page load).
  useEffect(() => {
    if (isAuthenticated && user?.themePreference && THEMES.includes(user.themePreference)) {
      setThemeState(user.themePreference);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.themePreference]);

  const setTheme = useCallback(
    (nextTheme) => {
      if (!THEMES.includes(nextTheme)) return;
      setThemeState(nextTheme);
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
      } catch (e) {
        // Ignore storage errors (private browsing, quota, etc.)
      }
      if (isAuthenticated) {
        apiClient.patch('/users/me', { themePreference: nextTheme }).catch(() => {
          // Non-fatal: the theme is already applied locally either way.
        });
      }
    },
    [isAuthenticated]
  );

  const value = useMemo(() => ({ theme, setTheme, themes: THEMES }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
